import { useEffect, useMemo, useRef, useState } from 'react';
import { FormProvider, useForm, useFormContext, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { ChevronDown } from 'lucide-react';
import type {
  DuplicateProjectMaterialOverrides,
  ProjectMaterialUsage,
  ProjectMaterialUsageMaterial,
} from '@kreart/shared';
import {
  MaterialAutosuggest,
  materialLabel,
} from '@/features/project-items/shared/pickers/CatalogAutosuggest';
import { useProjectMaterialUsage } from '@/features/projects/hooks';
import {
  cloneMaterialInitialValues,
  collectMaterialOverrides,
  materialComponentCount,
  overriddenComponentCount,
  swapTargetPaths,
  type CloneMaterialFormValues,
} from '@/features/projects/cloneMaterials';
import { cn } from '@/lib/cn';

/**
 * The clone dialog's optional "Materiale" section: the source project's
 * cabinets, drawers and panels, grouped in collapsible lists and reduced to
 * their boards, each behind its own picker — plus a quick-swap row per board
 * that fills every matching picker at once. What it collects rides on the
 * duplicate request as `materialOverrides`; touching nothing keeps the copy
 * faithful.
 *
 * It owns a form of its own (the pickers are RHF-bound), nested inside the
 * dialog's form via a separate `FormProvider` — the dialog's zod schema never
 * sees these fields, and the dialog pulls the result through `collectRef` at
 * submit time instead of through form state.
 */
export function CloneMaterialsSection({
  projectId,
  collectRef,
}: {
  projectId: string;
  collectRef: React.MutableRefObject<(() => DuplicateProjectMaterialOverrides | undefined) | null>;
}) {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(false);
  // Fetched on first expand, not on dialog open: a plain retitle-and-save
  // never waits on it. Once fetched it stays mounted so edits survive
  // collapsing the section again.
  const [everExpanded, setEverExpanded] = useState(false);
  const usageQuery = useProjectMaterialUsage(projectId, everExpanded);
  const usage = usageQuery.data;

  const form = useForm<CloneMaterialFormValues>({
    defaultValues: { cab: {}, drw: {}, pan: {}, swap: {} },
  });
  const hydratedRef = useRef(false);
  useEffect(() => {
    if (usage && !hydratedRef.current) {
      form.reset(cloneMaterialInitialValues(usage));
      hydratedRef.current = true;
    }
  }, [usage, form]);

  useEffect(() => {
    collectRef.current = () =>
      usage && hydratedRef.current ? collectMaterialOverrides(usage, form.getValues()) : undefined;
    return () => {
      collectRef.current = null;
    };
  }, [collectRef, usage, form]);

  // Re-renders only when a picker SELECTS (search typing is picker-local
  // state), so diffing the whole section for the badge stays cheap.
  const watched = useWatch({ control: form.control });
  const changeCount = useMemo(
    () =>
      usage && hydratedRef.current
        ? overriddenComponentCount(
            collectMaterialOverrides(usage, watched as Partial<CloneMaterialFormValues>),
          )
        : 0,
    [usage, watched],
  );

  const materialsById = useMemo(() => {
    const map = new Map<string, ProjectMaterialUsageMaterial>();
    for (const m of usage?.materials ?? []) map.set(m.id, m);
    return map;
  }, [usage]);
  const labelOf = (id: string) => {
    const m = materialsById.get(id);
    return m ? materialLabel(m) : id;
  };

  const isEmpty = usage && !usage.cabinets.length && !usage.drawers.length && !usage.panels.length;

  return (
    <div className="rounded-md border border-border">
      <button
        type="button"
        onClick={() => {
          setExpanded((prev) => !prev);
          setEverExpanded(true);
        }}
        aria-expanded={expanded}
        className="flex w-full items-center justify-between gap-2 px-3 py-2 text-left text-sm font-medium hover:bg-muted/50"
      >
        <span>{t('projects.create.materials.title')}</span>
        <span className="flex items-center gap-2">
          <span
            className={cn(
              'text-xs',
              changeCount > 0 ? 'font-medium text-primary' : 'text-muted-foreground',
            )}
          >
            {changeCount > 0
              ? t('projects.create.materials.changedBadge', { count: changeCount })
              : t('projects.create.materials.optionalBadge')}
          </span>
          <ChevronDown
            className={cn('h-4 w-4 text-muted-foreground transition-transform', {
              'rotate-180': expanded,
            })}
          />
        </span>
      </button>
      <div className={cn('border-t border-border', { hidden: !expanded })}>
        {usageQuery.isLoading && everExpanded ? (
          <p className="px-3 py-3 text-sm text-muted-foreground">{t('common.loading')}</p>
        ) : usageQuery.isError ? (
          <p className="px-3 py-3 text-sm text-destructive">
            {t('projects.create.materials.loadError')}
          </p>
        ) : isEmpty ? (
          <p className="px-3 py-3 text-sm text-muted-foreground">
            {t('projects.create.materials.empty')}
          </p>
        ) : usage ? (
          <FormProvider {...form}>
            <div className="space-y-3 px-3 py-3">
              <p className="text-xs text-muted-foreground">{t('projects.create.materials.hint')}</p>
              <QuickSwap usage={usage} labelOf={labelOf} />
              <ComponentGroup
                title={t('projects.create.materials.groups.cabinets')}
                count={usage.cabinets.length}
              >
                {usage.cabinets.map((c) => (
                  <ComponentRow
                    key={c.id}
                    label={c.roomName ? `${c.roomName} · ${c.name}` : c.name}
                  >
                    {c.constructionMode === 'PIECES' ? (
                      c.pieceMaterialIds.length ? (
                        c.pieceMaterialIds.map((fromId) => (
                          <MaterialAutosuggest
                            key={fromId}
                            name={`cab.${c.id}.p.${fromId}`}
                            label={t('projects.create.materials.slots.pieces', {
                              material: labelOf(fromId),
                            })}
                            roomyList
                          />
                        ))
                      ) : (
                        <p className="text-xs text-muted-foreground">
                          {t('projects.create.materials.noMaterials')}
                        </p>
                      )
                    ) : (
                      <>
                        <MaterialAutosuggest
                          name={`cab.${c.id}.m`}
                          label={t('projects.create.materials.slots.carcass')}
                          roomyList
                        />
                        <MaterialAutosuggest
                          name={`cab.${c.id}.f`}
                          label={t('projects.create.materials.slots.front')}
                          roomyList
                        />
                        <MaterialAutosuggest
                          name={`cab.${c.id}.b`}
                          label={t('projects.create.materials.slots.back')}
                          roomyList
                        />
                      </>
                    )}
                  </ComponentRow>
                ))}
              </ComponentGroup>
              <ComponentGroup
                title={t('projects.create.materials.groups.drawers')}
                count={usage.drawers.length}
              >
                {usage.drawers.map((d) => (
                  <ComponentRow
                    key={d.id}
                    label={`${d.cabinetName} — ${d.name ?? t('projects.create.materials.drawerFallback')}`}
                  >
                    <MaterialAutosuggest
                      name={`drw.${d.id}.m`}
                      label={t('projects.create.materials.slots.box')}
                      roomyList
                    />
                    <MaterialAutosuggest
                      name={`drw.${d.id}.f`}
                      label={t('projects.create.materials.slots.front')}
                      roomyList
                    />
                  </ComponentRow>
                ))}
              </ComponentGroup>
              <ComponentGroup
                title={t('projects.create.materials.groups.panels')}
                count={usage.panels.length}
              >
                {usage.panels.map((p) => (
                  <ComponentRow
                    key={p.id}
                    label={p.roomName ? `${p.roomName} · ${p.name}` : p.name}
                  >
                    <MaterialAutosuggest
                      name={`pan.${p.id}.m`}
                      label={t('projects.create.materials.slots.material')}
                      roomyList
                    />
                    <MaterialAutosuggest
                      name={`pan.${p.id}.f`}
                      label={t('projects.create.materials.slots.front')}
                      roomyList
                    />
                  </ComponentRow>
                ))}
              </ComponentGroup>
            </div>
          </FormProvider>
        ) : null}
      </div>
    </div>
  );
}

/**
 * One row per board the project uses anywhere: pick a replacement and every
 * matching per-component picker below is filled in one go. Matched against the
 * SOURCE usage, so re-applying a swap is deterministic even after individual
 * rows were adjusted by hand.
 */
function QuickSwap({
  usage,
  labelOf,
}: {
  usage: ProjectMaterialUsage;
  labelOf: (id: string) => string;
}) {
  const { t } = useTranslation();
  const form = useFormContext<CloneMaterialFormValues>();
  if (!usage.materials.length) return null;
  return (
    <div className="rounded-md bg-muted/50 p-2">
      <p className="mb-2 text-xs font-medium">{t('projects.create.materials.quickSwap.title')}</p>
      <div className="space-y-2">
        {usage.materials.map((m) => (
          <div key={m.id} className="grid items-end gap-2 sm:grid-cols-2">
            <div className="min-w-0 text-sm">
              <span className="block truncate">{labelOf(m.id)}</span>
              <span className="text-xs text-muted-foreground">
                {t('projects.create.materials.quickSwap.usedBy', {
                  count: materialComponentCount(usage, m.id),
                })}
              </span>
            </div>
            <MaterialAutosuggest
              name={`swap.${m.id}`}
              label={t('projects.create.materials.quickSwap.replaceWith')}
              roomyList
              onSelectedOption={(item) => {
                if (!item) return;
                for (const path of swapTargetPaths(usage, m.id)) {
                  form.setValue(path as never, item.id as never, { shouldDirty: true });
                }
              }}
            />
          </div>
        ))}
      </div>
    </div>
  );
}

function ComponentGroup({
  title,
  count,
  children,
}: {
  title: string;
  count: number;
  children: React.ReactNode;
}) {
  const [open, setOpen] = useState(false);
  if (count === 0) return null;
  return (
    <div className="rounded-md border border-border">
      <button
        type="button"
        onClick={() => setOpen((prev) => !prev)}
        aria-expanded={open}
        className="flex w-full items-center justify-between px-3 py-2 text-left text-sm hover:bg-muted/50"
      >
        <span className="font-medium">
          {title} <span className="text-muted-foreground">({count})</span>
        </span>
        <ChevronDown
          className={cn('h-4 w-4 text-muted-foreground transition-transform', {
            'rotate-180': open,
          })}
        />
      </button>
      {/* Rendered only while open: a big project mounts a lot of pickers. */}
      {open && (
        <div className="max-h-80 space-y-3 overflow-y-auto border-t border-border p-3">
          {children}
        </div>
      )}
    </div>
  );
}

function ComponentRow({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <p className="mb-1 truncate text-sm font-medium">{label}</p>
      <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">{children}</div>
    </div>
  );
}
